/**
 * Cross-Cloud Shared Resource Allocator (Patent Sub-system T)
 * Shapley value extended to shared RDS / S3 / Kafka — fair attribution.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, FlaskConical, Share2, Calculator } from 'lucide-react';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUsd = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const SharedCost = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/shared-cost/demo`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="shared-cost-page">
      <Helmet><title>Cross-Cloud Shared Resource Allocator | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-pink-300 border border-pink-500/30 rounded-md px-2 py-1 mb-5" data-testid="sc-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Shared Resource Allocator</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            Beyond Kubernetes: shared <span className="text-white font-semibold">RDS, S3, Kafka</span>. Existing tools split by proportional tag (gameable, unfair).
          </p>
          <p className="mt-3 text-sm text-slate-500">
            We extend <span className="text-pink-300">Shapley value</span> (the Sub-system M innovation) to non-K8s shared resources. Same mathematical fairness — efficiency, symmetry, dummy, additivity.
          </p>
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {data && (
          <>
            <div className="mt-10">
              <div className="text-xs uppercase tracking-widest text-pink-300 font-semibold mb-1">Total shared cost</div>
              <div className="text-3xl font-bold text-white" data-testid="sc-total">{fmtUsd(data.total_shared_cost_usd)}/month</div>
              <div className="text-sm text-slate-500 mt-1">across {data.allocations.length} shared resources</div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4" data-testid="sc-allocations">
              {data.allocations.map((alloc) => (
                <Card key={alloc.resource_id} className="bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Share2 className="w-5 h-5 text-pink-300" /> {alloc.label}
                    </CardTitle>
                    <CardDescription>
                      {alloc.resource_type} · {fmtUsd(alloc.monthly_cost_usd)}/mo · fixed {(alloc.fixed_cost_pct * 100).toFixed(0)}% / variable {(100 - alloc.fixed_cost_pct * 100).toFixed(0)}%
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                        <tr className="border-b border-white/10">
                          <th className="text-left p-2">Team</th>
                          <th className="text-right p-2">Usage share</th>
                          <th className="text-right p-2">Shapley fair</th>
                          <th className="text-right p-2">Naïve proportional</th>
                          <th className="text-right p-2">Delta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alloc.allocations.map((a) => (
                          <tr key={a.team} className="border-b border-white/5 hover:bg-slate-950/60">
                            <td className="p-2 text-sm text-white font-mono">{a.team}</td>
                            <td className="p-2 text-xs text-right text-slate-300 font-mono">{a.usage_share_pct}%</td>
                            <td className="p-2 text-sm text-right text-pink-300 font-mono font-semibold">{fmtUsd(a.shapley_allocation_usd)}</td>
                            <td className="p-2 text-sm text-right text-slate-500 font-mono">{fmtUsd(a.proportional_naive_usd)}</td>
                            <td className={`p-2 text-xs text-right font-mono ${a.delta_vs_proportional_usd > 0 ? 'text-amber-300' : a.delta_vs_proportional_usd < 0 ? 'text-emerald-300' : 'text-slate-300'}`}>
                              {a.delta_vs_proportional_usd > 0 ? '+' : ''}{fmtUsd(a.delta_vs_proportional_usd)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-3 text-[10px] text-slate-500 font-mono">{alloc.method}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="mt-8 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <Calculator className="w-5 h-5 text-pink-300 flex-shrink-0 mt-1" />
                  <div className="text-sm text-slate-200 leading-relaxed">
                    <span className="text-white font-semibold">Why Shapley?</span> Naïve proportional splits charge a team for what it directly used. Shapley charges a team for its <em>marginal contribution</em> averaged over every possible coalition — which provably satisfies fairness axioms (efficiency, symmetry, dummy, additivity). The delta column above shows how much your team is currently <em>overpaying</em> (+) or <em>underpaying</em> (−) under proportional accounting.
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default SharedCost;
